import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudiencePreview, AudienceSpec, Group, Role, Site } from '@up2d8/contracts';
import { Button, Card, CardTitle, Input, Select, cn } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import {
  audienceIncomplete,
  describedAudience,
  previewOrNothing,
} from '../../shared/audience-preview';

/**
 * De doelgroepkiezer, met een teller die meeloopt.
 *
 * Die teller is het punt van dit hele scherm. In het oude platform koos je rollen óf groepen,
 * zag je nergens hoeveel mensen dat waren, en ging een bericht in de praktijk naar "iedereen"
 * omdat niemand durfde te filteren. Hier staat er bij elke klik hoeveel mensen het worden, in
 * mensentaal — en de server rekent dat uit, niet de client.
 */
export function AudiencePicker({
  value,
  onChange,
  onPreview,
}: {
  value: AudienceSpec;
  onChange: (spec: AudienceSpec) => void;
  onPreview: (preview: AudiencePreview | null) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [search, setSearch] = useState('');

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/groups');
      return ((data as { items: Group[] } | undefined)?.items ?? []) as Group[];
    },
  });

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/sites', {});
      return ((data as { items: Site[] } | undefined)?.items ?? []) as Site[];
    },
  });

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles');
      return ((data as { roles: Role[] } | undefined)?.roles ?? []) as Role[];
    },
  });

  /**
   * Het aantal komt van de server, met een halve seconde vertraging.
   *
   * Zelf tellen in de browser zou betekenen dat het scherm iets anders zegt dan er straks
   * gebeurt — en juist dat verschil is wat het oude platform onbetrouwbaar maakte.
   */
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), 400);
    return () => window.clearTimeout(timer);
  }, [value]);

  const preview = useQuery({
    queryKey: ['audience-preview', debounced],
    queryFn: () =>
      previewOrNothing(debounced, async () => {
        const { data, error } = await api.POST('/api/v1/audience/preview', {
          body: { audience: debounced } as never,
        });
        if (error || !data) return null;
        return data as AudiencePreview;
      }),
  });

  useEffect(() => {
    onPreview(preview.data ?? null);
  }, [preview.data, onPreview]);

  const selectedGroups = value.type === 'filter' ? (value.groups ?? []) : [];
  const selectedRoles = value.type === 'filter' ? (value.roles ?? []) : [];
  const groupMatch = value.type === 'filter' ? value.groupMatch : 'any';
  const selectedSites = value.type === 'filter' ? (value.sites ?? []) : [];

  const filteredGroups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = groups.data ?? [];
    return needle === '' ? all : all.filter((group) => group.name.toLowerCase().includes(needle));
  }, [groups.data, search]);

  const filteredRoles = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = roles.data ?? [];
    return needle === ''
      ? all
      : all.filter((role) => roleName(role).toLowerCase().includes(needle));
  }, [roles.data, search]);

  /** Houdt de vorm geldig: een filter zonder keuzes bestaat niet, dat wordt "iedereen". */
  const setFilter = (next: {
    groups?: string[];
    roles?: string[];
    sites?: string[];
    groupMatch?: 'any' | 'all';
  }) => {
    const groupIds = next.groups ?? selectedGroups;
    const roleIds = next.roles ?? selectedRoles;
    const siteIds = next.sites ?? selectedSites;
    if (groupIds.length === 0 && roleIds.length === 0 && siteIds.length === 0) {
      onChange({ type: 'all' });
      return;
    }
    onChange({
      type: 'filter',
      groups: groupIds,
      roles: roleIds,
      sites: siteIds,
      groupMatch: next.groupMatch ?? groupMatch,
    });
  };

  const toggle = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((value) => value !== id) : [...list, id];

  return (
    <Card>
      <CardTitle>{t('posts.audience.title')}</CardTitle>

      <div className="mt-element flex flex-col gap-element">
        <div className="flex flex-wrap items-center gap-inline">
          <Button
            variant={value.type === 'all' ? 'primary' : 'secondary'}
            onClick={() => onChange({ type: 'all' })}
          >
            {t('posts.audience.everyone')}
          </Button>
          <Button
            variant={value.type === 'filter' ? 'primary' : 'secondary'}
            onClick={() =>
              onChange({
                type: 'filter',
                groups: [],
                roles: [],
                sites: [],
                groupMatch: 'any',
              } as AudienceSpec)
            }
          >
            {t('posts.audience.choose')}
          </Button>
        </div>

        {value.type === 'filter' && (
          <>
            <Input
              label={t('posts.audience.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('posts.audience.searchHint')}
            />

            {/*
              Vestigingen staan boven groepen en rollen, en alleen bij meer dan één (Stap 29).

              Het is de grofste grens: "Site B én Ploeg Nacht" leest als de nachtploeg van
              Kortrijk, en die volgorde maakt dat duidelijk. Bij één vestiging is de keuze geen
              keuze en staat ze er niet.
            */}
            {(sites.data?.length ?? 0) > 1 ? (
              <Chips
                title={t('sites.title')}
                empty={t('sites.emptyTitle')}
                items={(sites.data ?? []).map((site) => ({ id: site.id, label: site.name }))}
                selected={selectedSites}
                onToggle={(id) => setFilter({ sites: toggle(selectedSites, id) })}
              />
            ) : null}

            <div className="grid gap-element sm:grid-cols-2">
              <Chips
                title={t('groups.title')}
                empty={t('posts.audience.noGroups')}
                items={filteredGroups.map((group) => ({
                  id: group.id,
                  label: `${group.name} (${group.memberCount})`,
                }))}
                selected={selectedGroups}
                onToggle={(id) => setFilter({ groups: toggle(selectedGroups, id) })}
              />
              <Chips
                title={t('roles.title')}
                empty={t('posts.audience.noRoles')}
                items={filteredRoles.map((role) => ({ id: role.id, label: roleName(role) }))}
                selected={selectedRoles}
                onToggle={(id) => setFilter({ roles: toggle(selectedRoles, id) })}
              />
            </div>

            {selectedGroups.length > 1 && (
              <Select
                label={t('posts.audience.groupMatch')}
                hint={t('posts.audience.groupMatchHint')}
                value={groupMatch}
                onChange={(event) => setFilter({ groupMatch: event.target.value as 'any' | 'all' })}
              >
                <option value="any">{t('posts.audience.groupMatchAny')}</option>
                <option value="all">{t('posts.audience.groupMatchAll')}</option>
              </Select>
            )}
          </>
        )}

        {/* De uitkomst, in dezelfde woorden als straks bij het bericht staat. */}
        <div
          aria-live="polite"
          className="rounded-lg bg-brand-primary-tint px-3 py-2 text-content text-text-primary"
        >
          {preview.isPending ? (
            t('common.loading')
          ) : preview.data ? (
            <>
              <strong>{t('posts.audience.count', { count: preview.data.count })}</strong>
              {describedAudience(preview.data.description)}
              {preview.data.sample.length > 0 && (
                <span className="block text-small text-text-secondary">
                  {preview.data.sample.map((person) => person.displayName).join(', ')}
                  {preview.data.count > preview.data.sample.length && ' …'}
                </span>
              )}
            </>
          ) : (
            t('posts.audience.unknown')
          )}
        </div>

        {/* "Kiezen" zonder keuze kan niet bewaard worden (hertest 30 okt.): zeg dat hier. */}
        {audienceIncomplete(value) ? (
          <p className="text-small font-medium text-danger" data-testid="audience-empty">
            {t('posts.audience.empty')}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

function Chips({
  title,
  empty,
  items,
  selected,
  onToggle,
}: {
  title: string;
  empty: string;
  items: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="font-heading text-small font-medium text-text-secondary">{title}</legend>
      {items.length === 0 ? (
        <p className="text-small text-text-muted">{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item) => {
            const active = selected.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                aria-pressed={active}
                onClick={() => onToggle(item.id)}
                className={cn(
                  'rounded-pill border px-3 py-1.5 text-small transition-colors',
                  active
                    ? 'border-brand-primary bg-brand-primary text-text-on-dark'
                    : 'border-border bg-surface text-text-secondary hover:border-brand-primary',
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

/** De rolnaam in de taal van de gebruiker, met terugval op wat er wél staat. */
function roleName(role: Role): string {
  const names = role.name as Record<string, string | undefined>;
  return names.nl ?? names.en ?? names.fr ?? Object.values(names).find(Boolean) ?? '—';
}
